import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNodeByKey } from "lexical";
import { omit } from "lodash-es";
import { useMemo } from "react";
import { useSupabaseLoader } from "../../../../../../server/supabase/hooks";
import type { UseSupabaseLoaderState } from "../../../../../../server/supabase/hooks/types";
import { uuidv4 } from "../../../../../../utils/uuid";
import {
  type PresetPollOptionsType,
  type PresetPollQuestionWithOptions,
  SupabasePollService,
  SupabasePresetPollAnswerService,
  SupabasePresetPollQuestionService,
} from "../../../../../poll/server/supabase-poll-service";
import { type EditorModalState, useLexicalContext } from "../../../lexical-context";
import { $isPollNode } from "../../../nodes/poll/helpers";
import type { PollSchema, PollWrapperSchema } from "../types";

export type PollPresets = {
  options: PresetPollOptionsType[];
  questions: PresetPollQuestionWithOptions[];
};

export function usePollDataLoader(modalState?: EditorModalState): UseSupabaseLoaderState<{
  data: PollWrapperSchema;
  presets: PollPresets;
} | null> {
  const [editor] = useLexicalComposerContext();
  const { filterTags } = useLexicalContext();

  const channelTags = useMemo(() => {
    const rawTags = filterTags("");

    return Object.values(rawTags)
      .map((tag) => tag.groupTags)
      .flat()
      .filter((tag) => tag.category_key === null);
  }, [filterTags]);

  return useSupabaseLoader(
    async ({ supabase }) => {
      if (modalState?.type !== "poll") return null;

      const [
        { data: presetOptions, error: presetOptionsError },
        { data: presetQuestions, error: presetQuestionsError },
      ] = await Promise.all([
        new SupabasePresetPollAnswerService(supabase).getAll(null, {
          order: [{ column: "order" }],
        }),
        new SupabasePresetPollQuestionService(supabase).getAll(null, {
          order: [{ column: "order" }],
        }),
      ]);

      if (presetOptionsError || presetQuestionsError) {
        console.log("Error fetching poll presets", presetOptionsError || presetQuestionsError);
        throw new Error("Error fetching poll presets");
      }

      const questions = presetQuestions.map((q) => ({
        ...q,
        question: channelTags.reduce(
          (acc, tag) => acc.replace(`{{${tag.id}}}`, tag.fallback_value || ""),
          q.question,
        ),
      }));

      if (modalState.data.mode === "add") {
        return {
          presets: { options: presetOptions, questions },
          data: {
            question_type: { type: "custom" },
            poll: {
              id: uuidv4(),
              question: "",
              options: [{ text: "" }],
              show_poll_results: false,
              answer_type: {
                type: "custom",
                alignment: "horizontal",
                button_type: "primary_button",
              },
            },
          },
        };
      }

      const key = modalState.data.key;
      const nodeData = editor.read(() => {
        const node = $getNodeByKey(key);
        if (!$isPollNode(node)) return null;

        return {
          pollId: node.getPollId(),
          answer_type: node.getAnswerType(),
        };
      });

      if (!nodeData) return null;
      const { pollId, answer_type } = nodeData;

      const { data: polls, error: pollError } = await new SupabasePollService(
        supabase,
      ).getPollsWithOptions([pollId]);

      if (pollError) {
        console.log("Error fetching poll", pollError);
        throw new Error("Error fetching poll");
      }

      const poll = polls[0];

      if (!poll) {
        console.log("Poll not found");
        throw new Error("Poll not found");
      }

      const constructedPoll = {
        ...poll.poll,
        options: poll.poll.options.map((x) => omit(x, "id")),
        answer_type,
      };

      return {
        presets: { options: presetOptions, questions },
        data: {
          question_type: getPollType(constructedPoll, questions),
          poll: constructedPoll,
        },
      };
    },
    [modalState, editor, channelTags],
  );
}

function getPollType(
  poll: PollSchema,
  presets: PollPresets["questions"],
): PollWrapperSchema["question_type"] {
  if (poll.answer_type.type !== "preset") return { type: "custom" };

  const question = presets.find((q) => q.question === poll.question);

  if (!question) return { type: "custom" };

  if (
    question.answer_type_options.length === poll.options.length &&
    question.answer_type_options.every((opt, index) => opt.text === poll.options[index]!.text)
  ) {
    return { type: "preset", variation: question.id };
  }

  return { type: "custom" };
}
